import axios from "axios";
import config from "../../config/config";
import { Teacher, CreateTeacherInput, UpdateTeacherInput } from "./teacher.interfaces";
import * as mocks from "./teacher.mocks";

const API_URL = `${config.apiBaseUrl}/teachers`;

interface TeacherFilters {
  status?: 'Active' | 'Inactive';
}

interface TeacherQueryParams {
  page: number;
  pageSize: number;
  searchText?: string;
  status?: string;
}

const axiosClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: config.defaultTimeout,
});

axiosClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const apiGetTeachers = async (
  page: number = 1,
  pageSize: number = 10,
  searchText?: string,
  filters?: TeacherFilters
): Promise<Teacher[]> => {
  const params: TeacherQueryParams = {
    page,
    pageSize,
  };
  if (searchText) {
    params.searchText = searchText;
  }
  if (filters?.status) {
    params.status = filters.status;
  }
  const response = await axiosClient.get("/", { params });
  return response.data;
};

const apiGetTeacherById = async (id: string): Promise<Teacher> => {
  const response = await axiosClient.get(`/${id}`);
  return response.data;
};

const apiCreateTeacher = async (teacher: CreateTeacherInput): Promise<Teacher> => {
  const response = await axiosClient.post("/", teacher);
  return response.data;
};

const apiUpdateTeacher = async (id: string, teacher: UpdateTeacherInput): Promise<Teacher> => {
  const response = await axiosClient.patch(`/${id}`, teacher);
  return response.data;
};

const apiDeleteTeacher = async (id: string): Promise<void> => {
  await axiosClient.delete(`/${id}`);
};

export const getTeachers = async (
  page: number = 1,
  pageSize: number = 10,
  searchText?: string,
  filters?: TeacherFilters
): Promise<Teacher[]> => {
  if (config.useRealApi) {
    return apiGetTeachers(page, pageSize, searchText, filters);
  }
  // Quando usando mocks, ignora pageSize, searchText e filters
  return mocks.getTeachers(page);
};

export const getTeacherById = async (id: string): Promise<Teacher> => {
  return config.useRealApi ? apiGetTeacherById(id) : mocks.getTeacherById(id);
};

export const createTeacher = async (teacher: CreateTeacherInput): Promise<Teacher> => {
  return config.useRealApi ? apiCreateTeacher(teacher) : mocks.createTeacher(teacher);
};

export const updateTeacher = async (id: string, teacher: UpdateTeacherInput): Promise<Teacher> => {
  return config.useRealApi ? apiUpdateTeacher(id, teacher) : mocks.updateTeacher(id, teacher);
};

export const deleteTeacher = async (id: string): Promise<void> => {
  return config.useRealApi ? apiDeleteTeacher(id) : mocks.deleteTeacher(id);
};
